import * as React from 'react';
import { t } from '../../services/HelperService';
import { parseFieldMap } from './supplyOrderIntegrationDefinition';

/**
 * The supplier integration's FIELD-MAPPING TABLE, read-only: one row per documented supplier field (grouped by section) with
 * where the dispatch script takes it from - status (mapped / derived / constant / unmapped), source, rule, table. The same
 * table serves the assistant dialog's Mapping panel, the editor's Mapping tab and the Customizations list's detail.
 *
 * `fieldMap`: the parsed map ({ dispatchHash, rows }), a bare rows array, or JSON text (parseFieldMap). `stale`: the host
 * knows the dispatch script changed since the map was written (SupplyOrderIntegrationFieldMap.IsStale) - a banner says so;
 * `onRefresh` adds the "Refresh mapping" action to that banner (the editor opens an ask-intent assistant turn).
 */

const STATUS_STYLES = {
  mapped: { background: '#dcfce7', color: '#166534' },
  derived: { background: '#dbeafe', color: '#1e40af' },
  constant: { background: '#f3e8ff', color: '#6b21a8' },
  unmapped: { background: '#f1f5f9', color: '#64748b' },
};

const safeString = (v) => (v ?? '').toString();

/** SHA-256 (lowercase hex) of a dispatch script, normalised like the server (CRLF -> LF, trimmed); null when the Web Crypto API is unavailable. */
export const dispatchHashOf = async (script) => {
  const text = safeString(script).replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!text) return '';
  const subtle = typeof window !== 'undefined' ? window.crypto?.subtle : null;
  if (!subtle) return null;
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

/** True when the map was written for a different dispatch script (hash known on both sides and different). */
export const isFieldMapStale = (fieldMap, dispatchHash) => {
  const map = parseFieldMap(fieldMap);
  if (!map || !map.rows.length) return false;
  const current = safeString(dispatchHash).toLowerCase();
  if (!current) return false;
  return map.dispatchHash !== current;
};

export const FieldMapTable = ({ fieldMap, stale = false, onRefresh, refreshing = false, height, compact = false, emptyText }) => {
  const map = React.useMemo(() => parseFieldMap(fieldMap), [fieldMap]);
  const [showJson, setShowJson] = React.useState(false);

  if (!map || map.rows.length === 0) {
    return (
      <div style={{ padding: compact ? 8 : 16, fontSize: 12, color: '#64748b' }}>
        {emptyText ?? t('No field map yet. The assistant writes it with the dispatch output ("artifact: fieldMap"): one row per field of the supplier\'s documented order schema, saying where it comes from.')}
      </div>
    );
  }

  const sections = [];
  map.rows.forEach((r) => {
    const key = r.section || '';
    let s = sections.find((x) => x.key === key);
    if (!s) { s = { key, rows: [] }; sections.push(s); }
    s.rows.push(r);
  });
  const counts = FIELD_MAP_COUNTS(map.rows);

  const cell = { padding: compact ? '2px 6px' : '4px 8px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top', fontSize: compact ? 11 : 12 };
  const head = { ...cell, position: 'sticky', top: 0, background: '#f8fafc', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' };

  return (
    <div data-tagname="fieldMapTable" style={{ display: 'flex', flexDirection: 'column', gap: 6, height, minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: '#64748b', flexWrap: 'wrap' }}>
        <span>{t('{{count}} fields', { count: map.rows.length })}</span>
        {Object.entries(counts).filter(([, n]) => n > 0).map(([status, n]) => (
          <span key={status} style={{ ...STATUS_STYLES[status], padding: '0 6px', borderRadius: 8 }}>{n} {t(status)}</span>
        ))}
        <span style={{ flex: 1 }} />
        <button type="button" data-tagname="fieldMapToggleJson" onClick={() => setShowJson((v) => !v)} style={{ border: 'none', background: 'none', color: '#6f42c1', fontWeight: 600, cursor: 'pointer', fontSize: 11, padding: 0 }}>
          {showJson ? t('Table') : t('JSON')}
        </button>
      </div>
      {stale && (
        <div data-tagname="fieldMapStale" style={{ fontSize: 11, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1 }}>{t('The dispatch script changed since this mapping was written - it may be out of date.')}</span>
          {onRefresh && (
            <button type="button" data-tagname="fieldMapRefresh" disabled={refreshing} onClick={onRefresh} style={{ border: '1px solid #f59e0b', background: '#fff', color: '#92400e', borderRadius: 4, padding: '1px 8px', fontSize: 11, fontWeight: 600, cursor: refreshing ? 'default' : 'pointer' }}>
              {refreshing ? t('Refreshing...') : t('Refresh mapping')}
            </button>
          )}
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff' }}>
        {showJson ? (
          <pre style={{ margin: 0, padding: 8, fontSize: 11, whiteSpace: 'pre-wrap' }}>{JSON.stringify(map, null, 2)}</pre>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...head, textAlign: 'left' }}>{t('Supplier field')}</th>
                <th style={{ ...head, textAlign: 'left', width: 84 }}>{t('Status')}</th>
                <th style={{ ...head, textAlign: 'left' }}>{t('Source')}</th>
                <th style={{ ...head, textAlign: 'left' }}>{t('Rule')}</th>
                {!compact && <th style={{ ...head, textAlign: 'left', width: 120 }}>{t('Table')}</th>}
              </tr>
            </thead>
            <tbody>
              {sections.map((s) => (
                <React.Fragment key={s.key || '(none)'}>
                  {(sections.length > 1 || s.key) && (
                    <tr>
                      <td colSpan={compact ? 4 : 5} style={{ ...cell, background: '#f8fafc', fontWeight: 600, color: '#334155' }}>{s.key || t('(no section)')}</td>
                    </tr>
                  )}
                  {s.rows.map((r, i) => (
                    <tr key={`${s.key}_${r.target}_${i}`} data-tagname={`fieldMapRow_${r.target}`}>
                      <td style={{ ...cell, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{r.target}</td>
                      <td style={cell}><span style={{ ...(STATUS_STYLES[r.status] ?? STATUS_STYLES.unmapped), padding: '0 6px', borderRadius: 8, whiteSpace: 'nowrap' }}>{t(r.status || 'unmapped')}</span></td>
                      <td style={{ ...cell, fontFamily: 'monospace', wordBreak: 'break-all' }}>{r.source}</td>
                      <td style={cell}>{r.rule}</td>
                      {!compact && <td style={{ ...cell, fontFamily: 'monospace' }}>{r.table}</td>}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const FIELD_MAP_COUNTS = (rows) => rows.reduce((acc, r) => {
  const key = STATUS_STYLES[r.status] ? r.status : 'unmapped';
  acc[key] = (acc[key] ?? 0) + 1;
  return acc;
}, { mapped: 0, derived: 0, constant: 0, unmapped: 0 });
